import { useState, type FormEvent } from "react";

const NEWSLETTER_URL = "https://readdy.ai/api/form/daf9gkhtto0ceqqnh8i0";

const principles = [
  {
    icon: "ri-translate-2",
    title: "说人话",
    text: "不堆术语、不掉书袋，用普通网友都懂的语言把复杂的词讲清楚。",
  },
  {
    icon: "ri-history-line",
    title: "讲源头",
    text: "不只告诉你词义，更追根溯源，讲清它是怎么来的、为何流行。",
  },
  {
    icon: "ri-shield-check-line",
    title: "重求证",
    text: "涉及人物与事件的表述以官方通报与权威信源为准，倡导理性吃瓜。",
  },
  {
    icon: "ri-eye-line",
    title: "有边界",
    text: "适度补充法律常识与媒介素养，提醒围观与发声时的边界与责任。",
  },
];

interface FormStatus {
  type: "idle" | "success" | "error";
  message: string;
}

export default function About() {
  const [status, setStatus] = useState<FormStatus>({ type: "idle", message: "" });
  const [loading, setLoading] = useState(false);

  const onSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const fd = new FormData(form);

    // 反爬虫蜜罐：若被填了内容则假装成功，不真正发送
    const honey = ((fd.get("website_alt") as string) || "").trim();
    if (honey) {
      setStatus({ type: "success", message: "订阅成功！欢迎加入吃瓜百科的追更队伍。" });
      form.reset();
      return;
    }

    const payload = new URLSearchParams();
    fd.forEach((val, key) => {
      if (key !== "website_alt") payload.append(key, val as string);
    });

    setLoading(true);
    setStatus({ type: "idle", message: "" });
    try {
      const res = await fetch(NEWSLETTER_URL, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: payload.toString(),
      });
      const text = await res.text();
      let parsed: Record<string, unknown> | null = null;
      try {
        parsed = JSON.parse(text);
      } catch {
        parsed = null;
      }
      const msg = parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || "";
      const isSpam = typeof msg === "string" && msg.includes("spam");
      const ok = res.ok && parsed && (parsed.code === "OK" || parsed.code === "ok");
      if (ok && !isSpam) {
        setStatus({ type: "success", message: "订阅成功！每期「吃瓜周报」将准时送到你的邮箱。" });
        form.reset();
      } else {
        setStatus({
          type: "error",
          message:
            (typeof msg === "string" && msg ? `提交未成功：${msg}` : "") ||
            "提交未成功，请稍后再试，或检查邮箱格式是否正确。",
        });
      }
    } catch {
      setStatus({ type: "error", message: "网络似乎开了个小差，请稍后重试。" });
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="about" className="scroll-mt-16 bg-background-50">
      <div className="mx-auto max-w-7xl px-4 py-12 md:px-6 md:py-16">
        <div className="grid gap-10 lg:grid-cols-[minmax(0,6fr)_6fr] lg:gap-14">
          {/* 左：品牌介绍 */}
          <div>
            <span className="inline-flex items-center gap-1.5 text-xs font-semibold tracking-[0.2em] text-accent-600">
              <i className="ri-group-line text-sm leading-none"></i>
              关于我们 ABOUT
            </span>
            <h2 className="mt-3 font-heading text-2xl font-bold leading-snug text-foreground-950 md:text-3xl">
              一群爱看热闹、
              <br className="hidden sm:block" />
              更爱把事情讲明白的编辑
            </h2>
            <p className="mt-5 text-sm leading-7 text-foreground-600">
              「吃瓜百科」诞生于一个朴素的想法：热点天天有，可很多词大家天天看、却不一定真懂。塌房到底是什么意思？猹和闰土有什么关系？控评和洗地有什么区别？这些困惑，就是我们要回答的问题。
            </p>
            <p className="mt-4 text-sm leading-7 text-foreground-600">
              我们把散落在网络各处的流行词、行业黑话、典故与冷知识收拢起来，用一套统一、好懂的标准去梳理和解释，让每一次围观都更有深度，让吃瓜不再只停留在表面热闹。
            </p>

            <div className="mt-7 grid gap-4 sm:grid-cols-2">
              {principles.map((p) => (
                <div
                  key={p.title}
                  className="rounded-lg bg-background-100 p-4 ring-1 ring-background-200"
                >
                  <div className="flex h-9 w-9 items-center justify-center rounded-md bg-primary-100 text-primary-700">
                    <i className={`${p.icon} text-lg leading-none`}></i>
                  </div>
                  <h3 className="mt-3 font-heading text-base font-bold text-foreground-950">
                    {p.title}
                  </h3>
                  <p className="mt-1.5 text-sm leading-6 text-foreground-600">{p.text}</p>
                </div>
              ))}
            </div>
          </div>

          {/* 右：订阅 + 联系 */}
          <div className="flex flex-col gap-5">
            <div className="rounded-lg bg-gradient-to-br from-primary-600 to-primary-700 p-6 text-background-50 md:p-7">
              <div className="flex items-center gap-2 text-xs font-semibold tracking-[0.18em] text-background-100">
                <i className="ri-mail-send-line text-sm leading-none"></i>
                NEWSLETTER · 周报订阅
              </div>
              <h3 className="mt-3 font-heading text-xl font-bold text-background-50 md:text-2xl">
                每周一份「吃瓜周报」
              </h3>
              <p className="mt-2 text-sm leading-6 text-background-100">
                精选本周热议名词、新梗溯源与编辑部冷知识，用几分钟时间，轻松跟上网上的流行节奏。
              </p>

              <form
                id="newsletter-form"
                data-readdy-form="daf9gkhtto0ceqqnh8i0"
                onSubmit={onSubmit}
                className="mt-5"
              >
                <div className="flex flex-col gap-2 sm:flex-row">
                  <label className="flex flex-1 items-center gap-2 rounded-lg bg-background-50 px-3.5">
                    <i className="ri-mail-line text-base leading-none text-primary-600"></i>
                    <input
                      type="email"
                      name="email"
                      required
                      placeholder="请输入常用邮箱"
                      className="w-full bg-transparent py-2.5 text-sm text-foreground-900 placeholder:text-foreground-400 outline-none"
                    />
                  </label>
                  <button
                    type="submit"
                    disabled={loading}
                    className="whitespace-nowrap rounded-lg bg-accent-500 px-5 py-2.5 text-sm font-medium text-background-50 transition-colors hover:bg-accent-600 disabled:opacity-60 cursor-pointer"
                  >
                    {loading ? "提交中…" : "立即订阅"}
                  </button>
                </div>
                {/* 蜜罐字段 */}
                <input
                  type="text"
                  name="website_alt"
                  data-honey
                  tabIndex={-1}
                  autoComplete="off"
                  aria-hidden="true"
                  readOnly
                />
                {status.message && (
                  <p
                    className={`mt-3 flex items-start gap-1.5 rounded-md px-3 py-2 text-xs leading-5 ${
                      status.type === "success"
                        ? "bg-background-50/15 text-background-50"
                        : "bg-accent-100 text-accent-900"
                    }`}
                  >
                    <i
                      className={`${
                        status.type === "success" ? "ri-checkbox-circle-line" : "ri-alert-line"
                      } mt-0.5 text-sm leading-none`}
                    ></i>
                    {status.message}
                  </p>
                )}
              </form>
            </div>

            <div className="rounded-lg bg-background-100 p-6 ring-1 ring-background-200">
              <h3 className="flex items-center gap-2 font-heading text-base font-bold text-foreground-950">
                <i className="ri-customer-service-2-line text-lg leading-none text-primary-600"></i>
                联系与建议
              </h3>
              <p className="mt-2 text-sm leading-6 text-foreground-600">
                想投稿冷知识、报错纠偏，或有想查却搜不到的热词？欢迎写信告诉我们，编辑部会逐条阅读。
              </p>
              <div className="mt-4 space-y-2 text-sm">
                <a
                  href="mailto:hello@chigua-baike.cn"
                  className="flex items-center gap-2 text-primary-700 hover:text-primary-800 cursor-pointer"
                >
                  <i className="ri-mail-line text-base leading-none"></i>
                  hello@chigua-baike.cn
                </a>
                <p className="flex items-center gap-2 text-foreground-500">
                  <i className="ri-time-line text-base leading-none"></i>
                  编辑部每周二、周五集中查收邮件
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}